import { Accordion, Box, Button, TextField, Text, styled, keyframes, Switch, FormControlLabel } from '@streamelements/frontend-ui';
import { KeyboardArrowDownRounded, SvgIcon } from '@streamelements/frontend-icons';
import { providerIcons } from '../../ProviderIcon';
import { Destination, DestinationMetadataApiClient } from '../../../services/DestinationsApiClient';
import { AuthContextProvider, ServiceEnvironment } from '@streamelements/client-sdk-js';
import { ComponentProps, forwardRef, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { ToggleHideStreamKeyButton } from '../../../routes/_authenticated.add-output/-ToggleHideStreamKeyButton';
import { DisconnectButton, useDisconnectDestination } from '../DisconnectButton';
import { AllowedProvider, getIsOutputVideoEncoderOverrideEnabled } from '../../../constants/common';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { compositionsQueryOptions } from '../../../api/canvases';
import { FormValuesType } from './types';
import * as Combobox from '../../Combobox';
import { AudioCompositionsService } from '../../../services/AudioCompositionsService';
import { useEnableTiktokMutation } from '../../../api/enableTiktok';
import { useEnableStreamDestinationMutation } from '../../../api/enableDestination';
import { promisefy } from '../../../utils/promisefy';
import { streamDestinationsQueryOptions } from '../../../api/streamDestinations';
import { channelsOwnerAccessQueryOptions } from '../../../api/channels';
import { ObsEncoderEditor } from '../../OBS/ObsEncoderEditor';
import { getDefaultVideoEncoder } from '../../../constants/encoders';
import { ObsEncoderInfo, OutputInfo } from '../../../types/obs';
import { TransientError } from '../../../types/errors';

const highlight = keyframes({
  '0%': { boxShadow: '0 0 0 0 $colors$uiPrimary50' },
  '10%': { boxShadow: '0 0 0 4px $colors$uiPrimary50' },
  '80%': { boxShadow: '0 0 0 4px $colors$uiPrimary50' },
  '100%': { boxShadow: '0 0 0 0 $colors$uiPrimary50' },
});

const Root = styled(Accordion.Item as React.ElementType, {
  bgc: '$uiSecondary',
  br: '0.25rem',
  border: '$divider 1px solid',

  '&[data-highlight="true"]': {
    animation: `${highlight} 2s ease-in-out`,
    animationDelay: '0.5s',
  },
});

const Header = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
  padding: '1rem',
});

const TriggerButton = forwardRef<HTMLButtonElement, ComponentProps<typeof Button>>(function Trigger(props, ref) {
  return (
    <Accordion.Trigger asChild>
      <Button
        {...props}
        ref={ref}
        iconButton
        color="neutral"
        variant="ghost"
        css={{
          '&[data-state="open"] svg': { transform: 'rotate(180deg)' },
        }}
      >
        <KeyboardArrowDownRounded />
      </Button>
    </Accordion.Trigger>
  );
});

function Action({
  outputId,
  authContextProvider,
  serviceEnvironment,
  destinationMetadata,
  disabled,
}: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
  destinationMetadata: Destination;
  outputId: string;
  disabled?: boolean;
}) {
  const queryClient = useQueryClient();

  const enableDestinationMutation = useEnableStreamDestinationMutation({
    authContextProvider,
    serviceEnvironment,
    source: { id: destinationMetadata.id },
    streamDestination: destinationMetadata,
  });

  const enableTiktokDestinationMutation = useEnableTiktokMutation({
    authContextProvider,
    serviceEnvironment,
    streamDestination: destinationMetadata,
  });

  const isConnected = destinationMetadata.outputs.find(
    (output) =>
      output.id === outputId && output.isEnabled && output.auxiliaryData?.destinationId === destinationMetadata.id
  );

  if (isConnected) {
    return (
      <DisconnectButton
        authContextProvider={authContextProvider}
        serviceEnvironment={serviceEnvironment}
        destinationMetadata={destinationMetadata}
        disabled={disabled}
      />
    );
  }

  if (destinationMetadata.provider === 'tiktok') {
    return (
      <Button
        density="compact"
        onClick={() =>
          enableTiktokDestinationMutation.mutate(void 0, {
            onSuccess: () => {
              queryClient.refetchQueries(
                streamDestinationsQueryOptions({
                  authContextProvider,
                  serviceEnvironment,
                })
              );
            },
          })
        }
        disabled={enableTiktokDestinationMutation.isPending}
      >
        Connect
      </Button>
    );
  }

  return (
    <Button
      density="compact"
      onClick={() => {
        // It's fine to pass an empty string because we have `null` stream destination.
        // connectMutation.mutate({ outputId: '' });
        enableDestinationMutation.mutate(
          {
            outputId,
          },
          {
            onSuccess: () => {
              queryClient.refetchQueries(
                streamDestinationsQueryOptions({
                  authContextProvider,
                  serviceEnvironment,
                })
              );
            },
            onError: () => {
              queryClient.refetchQueries(
                channelsOwnerAccessQueryOptions({
                  authContextProvider,
                  serviceEnvironment,
                })
              );
            },
          }
        );
      }}
      disabled={enableDestinationMutation.isPending || disabled}
    >
      Connect
    </Button>
  );
}

const StyledForm = styled('form', {
  display: 'grid',
  gap: '1rem',
  minHeight: '1rem',
  padding: '1rem',
});

type Props = {
  destination: Destination;
  output: Destination['outputs'][number];
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
  disabled?: boolean;
};

async function setOutputProperties({ formValues }: { formValues: FormValuesType }) {
  const { audioTracks, videoEncoders } = formValues;

  const result: FormValuesType = { ...formValues, audioTracks: undefined, audioCompositionId: 'default', videoEncoders: undefined };

  // Check audio tracks
  if (audioTracks?.length) {
    const { id: audioCompositionStorageId } = formValues;

    const audioCompositions = await AudioCompositionsService.getActiveCompositions();

    if (!audioCompositions[audioCompositionStorageId]) {
      const audioEncoders = await promisefy(window.host.getAvailableAudioEncoders)();

      const aacEncoder = audioEncoders.find((encoder) => encoder.id === 'ffmpeg_aac')!;
      const encoderClass = await promisefy(window.host.getEncoderProperties)(aacEncoder);

      if (!encoderClass) {
        throw new Error('Failed to get encoder class');
      }

      const audioComposition = await AudioCompositionsService.registerComposition({
        id: audioCompositionStorageId,
        name: `${formValues.name} - StreamElements Audio Composition`,
        audioTracks: Array(6).map((_, index) => ({
          id: index,
          name: `Track ${index + 1}`,
        })),
        streamingAudioEncoder: encoderClass,
        recordingAudioEncoder: encoderClass,
      });

      if (!audioComposition) {
        throw new Error('Failed to create audio composition');
      }

      audioCompositions[audioCompositionStorageId] = audioComposition;
    }

    const audioComposition = audioCompositions[audioCompositionStorageId];

    if (audioComposition) {
      result.audioTracks = audioTracks;
      result.audioCompositionId = audioComposition.id;
    }
  }

  // Check video encoders
  if (videoEncoders?.length) {
    result.videoEncoders = videoEncoders;
  }

  console.log('result: ', result);

  return result;
}

function Form({ destination, output, authContextProvider, serviceEnvironment, disabled }: Props) {
  const { provider } = destination;
  const [streamKeyVisible, setStreamKeyVisible] = useState(false);
  const [isOutputVideoEncoderOverrideEnabled, setIsOutputVideoEncoderOverrideEnabled] = useState(false);
  const previousValuesRef = useRef(output);
  previousValuesRef.current = output;

  useEffect(() => {
    const abortController = new AbortController();

    getIsOutputVideoEncoderOverrideEnabled().then((enabled) => {
      if (abortController.signal.aborted) {
        return;
      }

      if (isOutputVideoEncoderOverrideEnabled === enabled) {
        return;
      }

      setIsOutputVideoEncoderOverrideEnabled(enabled);
    });

    return () => {
      abortController.abort();
    };
  }, [isOutputVideoEncoderOverrideEnabled, setIsOutputVideoEncoderOverrideEnabled]);

  const form = useForm<FormValuesType>({
    disabled: !!disabled,
    defaultValues: async () => {
      return {
        ...output,
      };
    },
    resolver: zodResolver(
      z.object({
        id: z.string(),
        name: z.string(),
        audioTracks: z.array(z.number()).optional(),
        videoEncoders: z.array(z.unknown()).optional(),
        videoCompositionId: z.string().optional(),
        audioCompositionId: z.string().optional(),
        streamingSettings: z.object({
          serverUrl: z.string(),
          streamKey: z.string(),
        }),
      })
    ),
  });

  const onSubmit = useMutation({
    mutationKey: ['updateDestination', destination],
    mutationFn: async (formValues: FormValuesType) => {
      const previousAudioCompositionId = previousValuesRef.current.audioCompositionId;
      const newAudioCompositionId = formValues.audioCompositionId;

      const {
        streamingSettings, //: { serverUrl, streamKey },
        audioTracks,
        videoEncoders,
        audioCompositionId,
      } = await setOutputProperties({ formValues });

      await new DestinationMetadataApiClient({
        destinationMetadata: destination,
        authContextProvider,
        serviceEnvironment,
      }).updateDestination({
        outputs: destination.outputs.map((item) => {
          if (item.id === output.id) {
            return {
              ...output,
              audioTracks,
              videoEncoders,
              audioCompositionId,
              streamingSettings,
            };
          } else {
            return {
              ...item,
            };
          }
        }),
      });

      if (
        [
          !!previousAudioCompositionId,
          previousAudioCompositionId !== newAudioCompositionId,
          previousAudioCompositionId !== 'default',
        ].every(Boolean)
      ) {
        console.log(
          'Removing previous output audio composition',
          previousAudioCompositionId,
          'because the output no longer uses it.'
        );
        await AudioCompositionsService.removeComposition({ audioCompositionId: previousAudioCompositionId! });
      }
    },
    onSuccess: () => {
      form.reset((prev) => prev);
    },
  });

  const disconnectDestinationMutation = useDisconnectDestination({
    authContextProvider,
    serviceEnvironment,
    destinationMetadata: destination,
  });

  const { data: audioCompositions } = useQuery({
    queryKey: ['audioCompositions'],
    queryFn: () => AudioCompositionsService.getAllAudioCompositions(),
    initialData: {
      default: {
        id: 'default',
        isObsNativeComposition: true,
        name: 'Default',
        audioTracks: [],
        streamingAudioEncoder: {} as never,
        recordingAudioEncoder: {} as never,
        canRemove: false,
      },
    },
  });

  const audioTracksField = form.watch('audioTracks');
  const videoEncodersField = form.watch('videoEncoders');
  const audioCompositionIdField = form.watch('audioCompositionId');
  const { isValid } = form.formState;

  const hasCustomAudioComposition = audioCompositionIdField && audioCompositionIdField !== 'default';

  const videoCompositionId = form.watch('videoCompositionId') || 'default';

  return (
    <StyledForm
      onSubmit={form.handleSubmit((values) => {
        onSubmit.mutate(values);
      }, console.error)}
    >
      {provider === 'custom' && (
        <>
          <Controller
            control={form.control}
            name="streamingSettings.serverUrl"
            render={({ field }) => {
              return (
                <TextField.Single
                  disabled={field.disabled}
                  label="Server URL"
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  inputProps={{
                    onBlur: field.onBlur,
                  }}
                />
              );
            }}
          />
          <Controller
            control={form.control}
            name="streamingSettings.streamKey"
            render={({ field }) => {
              return (
                <TextField.Single
                  disabled={field.disabled}
                  label="Stream Key"
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  inputProps={{
                    onBlur: field.onBlur,
                    autoComplete: 'off',
                  }}
                  type={streamKeyVisible ? 'text' : 'password'}
                >
                  <TextField.Adornment>
                    <ToggleHideStreamKeyButton isVisible={streamKeyVisible} setVisible={setStreamKeyVisible} />
                  </TextField.Adornment>
                </TextField.Single>
              );
            }}
          />
        </>
      )}

      <Text.Subtitle weight="bold">Audio</Text.Subtitle>

      <Combobox.ComboBoxRoot
        value={hasCustomAudioComposition ? audioTracksField?.map((trackIndex) => trackIndex.toString()) || [] : []}
        multiple
        inputValue={
          hasCustomAudioComposition && audioTracksField?.length
            ? audioTracksField
                ?.map((trackIndex) => audioCompositions.default?.audioTracks?.[Number(trackIndex)]?.name ?? '')
                .join('; ')
            : 'Same as OBS'
        }
        onValueChange={(next) => {
          const value = next
            .filter((track) => track !== '')
            .map((val) => Number(val))
            .slice(0, 2)
            .sort();

          if (!value.length) {
            form.setValue('audioTracks', undefined, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
            form.setValue('audioCompositionId', 'default', {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
            return;
          }

          form.setValue('audioTracks', value);
          form.setValue('audioCompositionId', output.id, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
        }}
      >
        <Combobox.ComboBoxTrigger label="Audio tracks" inputProps={{ readOnly: true }} disabled={disabled} />
        <Combobox.ComboBoxContent>
          {audioCompositions.default.audioTracks.map((track, index) => {
            return (
              <Combobox.ComboBoxOption value={index.toString()} key={track.id}>
                {track.name}
              </Combobox.ComboBoxOption>
            );
          })}
        </Combobox.ComboBoxContent>
      </Combobox.ComboBoxRoot>

      { isOutputVideoEncoderOverrideEnabled && (<>
        <Text.Subtitle weight="bold" css={{ mt: '0.5rem' }}>Encoding</Text.Subtitle>

        <FormControlLabel>
          <Switch
            disabled={disabled}
            checked={typeof videoEncodersField?.[0] === 'object'}
            onCheckedChange={async (checked) => {
              if (!checked) {
                form.setValue('videoEncoders', undefined, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,     
                });

                return;
              }

              const defaultVideoEncoder = await getDefaultVideoEncoder();

              form.setValue('videoEncoders', [
                defaultVideoEncoder,
              ], {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true,     
              });
            }}
          />
          <div>
            <Text.Body>Override Settings</Text.Body>
            <Text.Body variant="caption" css={{ color: '$textPrimary50'}}>Enabling this feature will require more resources from your device</Text.Body>
          </div>
        </FormControlLabel>

        {typeof videoEncodersField?.[0] === 'object' && (<>
          <ObsEncoderEditor
            disabled={disabled}
            type="video"
            defaultValue={videoEncodersField?.[0] as ObsEncoderInfo}
            videoCompositionId={videoCompositionId}
            showScaleVideoFrame={true}
            onChange={(next) => {
              const data = structuredClone(videoEncodersField) ?? [];

              data[0] = next;

              form.setValue('videoEncoders', data, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true,     
              });
            }}
          />
        </>)}
      </>)}

      <Box css={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '1rem' }}>
        {provider === 'custom' && (
          <Button
            disabled={disabled}
            color="error"
            variant="ghost"
            density="compact"
            onClick={() => disconnectDestinationMutation.mutate()}
          >
            Delete output
          </Button>
        )}
        <Button
          density="compact"
          color="primary"
          type="submit"
          disabled={!isValid || onSubmit.isPending || disabled || !form.formState.isDirty}
          css={{ ml: 'auto' }}
        >
          Save
        </Button>
      </Box>
    </StyledForm>
  );
}

function Trigger() {
  return (
    <Box
      css={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '1rem',
        padding: '1rem',
        pr: '0.675rem',
        transition: 'padding 0.2s ease-in-out',
        borderTop: '$divider 1px solid',

        '[data-state="open"] &': {
          pb: 0,
        },
      }}
    >
      <Text.Subtitle css={{ margin: 0 }} weight="bold">
        Output settings
      </Text.Subtitle>
      <Accordion.Trigger asChild>
        <TriggerButton />
      </Accordion.Trigger>
    </Box>
  );
}

function ListItem({ children, disabled, ...props }: PropsWithChildren<Props>) {
  const { id, provider, displayName } = props.destination;
  const ProviderIcon = provider ? providerIcons[provider as AllowedProvider] : null;

  const rootRef = useRef<HTMLDivElement>(null);
  const { data: canvases } = useQuery({
    ...compositionsQueryOptions,
    initialData: {} as never,
    refetchOnWindowFocus: true,
  });

  const { videoCompositionId = 'default' } = props.output;

  const canvasName =
    videoCompositionId !== 'default' || !canvases?.[videoCompositionId]
      ? canvases?.[videoCompositionId]?.name + ' - StreamElements Canvas'
      : 'Default canvas';

  useEffect(() => {
    const element = rootRef.current;

    if (!element) {
      return;
    }

    function scrollIntoView() {
      return new Promise<void>((resolve, reject) => {
        if (element?.getAttribute('data-state') === 'open') {
          element?.scrollIntoView({ behavior: 'smooth', block: 'end' });
          resolve();
          return;
        }

        reject(new TransientError('Element requested to scroll into view is not open'));
      });
    }

    scrollIntoView()
      .then(() => {
        if (!rootRef.current) {
          return;
        }

        rootRef.current.addEventListener(
          'animationend',
          () => {
            rootRef.current?.removeAttribute('data-highlight');
          },
          { once: true }
        );

        rootRef.current?.setAttribute('data-highlight', 'true');
      })
      .catch(() => void 0);
  }, []);

  const isPrimaryOutput = !!((props.output as OutputInfo).isObsNativeOutput || props.output.auxiliaryData?.isPrimaryOutput);

  return (
    <Root value={props.output.id} ref={rootRef}>
      <Accordion.Header>
        <Header css={provider === 'custom' ? { gap: '0.75rem' } : undefined}>
          {ProviderIcon ? (
            <ProviderIcon size={provider === 'custom' ? 'lg' : 'md'} />
          ) : (
            <SvgIcon>
              <path d="M20 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4M4 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4M12 22a2 2 0 1 0 0-4 2 2 0 0 0 0 4M7.89 14.65l-2.94 2.93a.996.996 0 1 0 1.41 1.41l2.94-2.93a.996.996 0 1 0-1.41-1.41M6.41 4.94A.996.996 0 1 0 5 6.35l2.93 2.94c.39.39 1.02.39 1.42 0 .38-.39.38-1.02-.01-1.41zM16.12 14.65c-.39-.39-1.02-.39-1.42 0a.996.996 0 0 0 0 1.41L17.64 19a.996.996 0 1 0 1.41-1.41zM16.06 9.33l2.99-2.98c.39-.4.39-1.03 0-1.42a.996.996 0 0 0-1.41 0l-2.99 2.98c-.39.39-.39 1.02 0 1.42.39.39 1.02.39 1.41 0M12 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4" />
            </SvgIcon>
          )}
          <Box css={{ flex: 1 }}>
            <Text.Subtitle weight="bold" css={{ margin: 0 }}>
              {displayName}
            </Text.Subtitle>
            <Text.Body css={{ color: '$textPrimary50', margin: 0 }} data-canvas-name={canvases?.[id]?.name}>
              {canvasName}
            </Text.Body>
          </Box>

          {children}
        </Header>
        { !isPrimaryOutput && (<Trigger />) }
      </Accordion.Header>
      { !isPrimaryOutput && (
        <Accordion.Content>
          <Form {...props} disabled={disabled} />
        </Accordion.Content>
      ) }
    </Root>
  );
}

export { Root, ListItem as Content, Action, Header, Trigger };
